/**
 * U1.4. The first sign-in: one screen between a new person and Today.
 *
 * Drawn by `App.tsx` *instead of* the shell while the signed-in person's
 * profile row has no `onboarded_at` (`shouldOnboard`, `lib/onboarding.ts`),
 * so there is nothing to navigate to and no route to land on by accident.
 *
 * The form writes nothing as it is filled. Done writes the draft and
 * `onboarded_at`; Later writes what was filled in (a blank name is left
 * alone) and `onboarded_at`, so the screen never comes back — the same form
 * is the "You" section of Account for as long as the person wants it.
 * Only the name is asked for, and only by Done: Later skips everything.
 *
 * The write is `useUpdateProfile`, which patches the cached row on the tap.
 * That is what ends this screen: `onboarded_at` appears in the cache, the
 * gate stops drawing it, and the app is there on the same frame. The hash is
 * set to `#/today` first so the person lands on Today whatever hash the
 * invite link left behind.
 *
 * U2.4 adds the second step, "What do you go out for?" (`TasteStep` below).
 * Which step is showing is not state of this file: it is `nextStep(profile,
 * needsTaste(...))`, read off the cached profile. Done on the first step
 * writes the name into that cache, so the screen moves on by itself, and a
 * person who comes back with a name and no taste vector lands on the second
 * step directly. Neither step writes anything until its Done / Save; Later on
 * either writes `onboarded_at` and leaves the app to rank from defaults (U2.3).
 * The vector is saved before `onboarded_at` is, so a save the server refuses
 * keeps the person here with a message instead of in the app with nothing kept.
 */
import { useEffect, useRef, useState } from 'react'
import { Banner, Button } from '../ui'
import { KiezMark } from '../components/KiezMark'
import { ProfileForm } from '../components/ProfileForm'
import { TasteForm } from '../components/TasteForm'
import { useProfile, type Profile } from '../lib/profile'
import { useActiveTaste, useVenues } from '../lib/queries'
import { useSaveTaste, useUpdateProfile } from '../lib/mutations'
import { goHash } from '../lib/router'
import { draftFrom, finishPatch, needsTaste, nextStep, type Draft } from '../lib/onboarding'
import { draftFromVector, vectorFrom, type TasteDraft } from '../lib/tasteForm'
import '../components/ProfileForm.css'

export function Onboarding() {
  const profile = useProfile()
  const tasteQ = useActiveTaste()
  // `undefined` until the read has settled: `needsTaste` asks rather than guesses.
  const taste = needsTaste(tasteQ.isSuccess ? tasteQ.data !== null : undefined)
  // Latched, like the gate in `App.tsx`: saving the vector patches the taste
  // cache, which makes `needsTaste` false, which would answer 'profile' and
  // unmount this step mid-save — taking the pending write's error handling and
  // the person's answers with it. Once the second step has been shown it stays
  // until `onboarded_at` ends the screen.
  const onTaste = useRef(false)
  if (nextStep(profile, taste) === 'taste') onTaste.current = true
  return onTaste.current ? <TasteStep profile={profile} /> : <ProfileStep taste={taste} />
}

function ProfileStep({ taste }: { taste: boolean }) {
  const profile = useProfile()
  const { updateProfile } = useUpdateProfile()
  const [draft, setDraft] = useState<Draft>(() => draftFrom(profile))
  const [nameError, setNameError] = useState<string | undefined>()

  function finish(how: 'done' | 'later') {
    if (how === 'done' && !draft.name.trim()) {
      setNameError('Add a name to finish, or choose Later.')
      document.querySelector<HTMLInputElement>('.onboard .u-input')?.focus()
      return
    }
    updateProfile(finishPatch(draft, how, new Date().toISOString(), taste))
    // Later ends onboarding; Done leaves the taste step to follow, when one applies.
    if (how === 'later' || !taste) goHash('#/today')
  }

  return (
    <div className="onboard">
      <div className="onboard__page">
        <header className="onboard__head">
          <KiezMark className="onboard__mark" />
          <h1 className="screen-title">Welcome to Kiez</h1>
          <p className="onboard__line">
            A few answers make it yours. Only a name is needed, and all of it can be changed later under Account.
          </p>
        </header>

        <ProfileForm
          draft={draft}
          onChange={d => { setDraft(d); if (nameError && d.name.trim()) setNameError(undefined) }}
          nameError={nameError}
        />
      </div>

      <div className="onboard__bar">
        <div className="onboard__barin">
          <Button variant="quiet" onClick={() => finish('later')}>Later</Button>
          <Button block onClick={() => finish('done')}>{taste ? 'Next' : 'Done'}</Button>
        </div>
      </div>
    </div>
  )
}

/** Step two. The form is seeded from a vector the person already has (a
 *  returning visit to a half-finished onboarding) and is not re-seeded while
 *  open, as Account's forms are not. */
function TasteStep({ profile }: { profile: Profile }) {
  const tasteQ = useActiveTaste()
  const venuesQ = useVenues()
  const save = useSaveTaste()
  const { updateProfile } = useUpdateProfile()
  const [draft, setDraft] = useState<TasteDraft>(() => draftFromVector(tasteQ.data?.vector))
  const [error, setError] = useState<string | undefined>()
  // The first step is a long page and Next is at its foot: begin this one at the top.
  useEffect(() => { window.scrollTo(0, 0) }, [])

  function leave() {
    updateProfile({ onboarded_at: new Date().toISOString() })
    goHash('#/today')
  }

  async function finish() {
    setError(undefined)
    try {
      await save.mutateAsync({ vector: vectorFrom(draft, profile.english_only, venuesQ.data?.list ?? []) })
    } catch {
      setError("Couldn't save your answers. Try again, or choose Later.")
      return
    }
    leave()
  }

  return (
    <div className="onboard">
      <div className="onboard__page">
        <header className="onboard__head">
          <KiezMark className="onboard__mark" />
          <h1 className="screen-title">What do you go out for?</h1>
          <p className="onboard__line">
            This is how Kiez orders what it shows you. Nothing is hidden for good, and all of it can be changed later under Account.
          </p>
        </header>

        <TasteForm draft={draft} onChange={setDraft} englishOnly={profile.english_only} />
        {error && <Banner tone="bad" className="onboard__error">{error}</Banner>}
      </div>

      <div className="onboard__bar">
        <div className="onboard__barin">
          <Button variant="quiet" onClick={leave} disabled={save.isPending}>Later</Button>
          <Button block busy={save.isPending} onClick={finish}>Save</Button>
        </div>
      </div>
    </div>
  )
}
